import { LicenseType, VehicleType, Zone } from '@/interfaces/new-license-request/new-license-request-interfaces';
import { normalize } from '@/utils/normalizeString';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { RecordType } from '../../form.interfaces';
import { NewEquipmentRow } from '../new-record.interfaces';
import { mustSelectOne } from '../new-record.validators';

type Asset = 'SPC' | 'MEL';
@Component({
  selector: 'app-equipment[assetName]',
  templateUrl: './equipment.component.html',
  styleUrls: ['./equipment.component.css'],
})
export class EquipmentComponent implements OnInit {

  @Input() emptyTitles: boolean = false;
  @Input() selectedOption: RecordType = 'NONE';
  @Input() vehicleTypes: VehicleType[] = [];
  @Input() bannedVehicleTypes: number[] = [];
  @Input() colorInputs: 'GREY' | 'WHITE' = 'GREY';
  // @Input() plantaDefaultSelected: boolean = false;

  _assetName: Asset = 'SPC';
  @Input() get assetName(): Asset {
    return this._assetName;
  }
  set assetName(name: Asset) {
    if (name === 'MEL') {
      this.setBrandAndModelValidators(true);
      this.resetForm();
    }
    else {
      this.setBrandAndModelValidators(false);
      this.resetForm();
    }
    this._assetName = name;
  }

  @Input()
  set licenseTypes(licenseTypes: LicenseType[]) {
    const formArray = this.newEquipmentForm.controls.licenseTypes as FormArray;

    for (let i = 0; i < formArray.length; i++) {
      formArray.removeAt(i);
    }

    licenseTypes.map((item) => {
      formArray.push(new FormGroup({ id: new FormControl(item.id), name: new FormControl(item.name), active: new FormControl(false) }));
    });

    this._licenseTypes = licenseTypes;
  }
  get licenseTypes(): LicenseType[] {
    return this._licenseTypes;
  }
  _licenseTypes: LicenseType[] = [];

  @Input()
  set conductionZones(conductionZones: Zone[]) {
    const formArray = this.newEquipmentForm.controls.conductionZones as FormArray;

    for (let i = 0; i < formArray.length; i++) {
      formArray.removeAt(i);
    }

    conductionZones.filter((item) => {

      const enabled = true;
      const active = false;
      let isHidden = false;

      if (normalize(item.name) === normalize('Solo Operar')) {
        isHidden = true;
      }

      formArray.push(new FormGroup({
        id: new FormControl(item.id),
        name: new FormControl(item.name),
        active: new FormControl(active),
        isEnabled: new FormControl(enabled),
        isHidden: new FormControl(isHidden),
      }));
    });

    this._conductionZones = conductionZones;
    this.conductionZonesSlice = Math.ceil(conductionZones.length / 2);
  }
  get conductionZones(): Zone[] {
    return this._conductionZones;
  }
  _conductionZones: Zone[] = [];

  conductionZonesSlice = 1;

  @Output() addEquipment = new EventEmitter<NewEquipmentRow>();

  newEquipmentForm: FormGroup = new FormGroup({
    vehicleType: new FormControl('', [Validators.required]),
    brand: new FormControl(''),
    model: new FormControl(''),
    licenseTypes: new FormArray([], [mustSelectOne()]),
    conductionZones: new FormArray([], [mustSelectOne()]),
  });

  get vehicleTypesToList(): VehicleType[] {
    // return this.vehicleTypes.filter(({ id }) => !this.bannedVehicleTypes.includes(id));
    return this.vehicleTypes;
  }

  get isModelAndBrandRequired(): boolean {
    return this.assetName === 'MEL';
  }

  constructor() { }

  ngOnInit(): void {
    type CheckFormControlValues = { id: number; name: string; active: boolean };
    this.newEquipmentForm.controls.licenseTypes.valueChanges.subscribe({
      next: (licenseTypes: CheckFormControlValues[]) => {
        const setCheckToPlanta = () => {
          const isConductionSelected = licenseTypes.some((licenseType) => normalize(licenseType.name) === normalize('Conducción') && licenseType.active);
          let plantaControl: FormControl | null | undefined;
          const conductionZonesControls = this.newEquipmentForm.controls.conductionZones as FormArray;
          for (let i = 0; i < conductionZonesControls.length; i++) {
            const control = conductionZonesControls.at(i);
            if (control.value.name.includes('Planta')) {
              plantaControl = control as FormControl;
            }
          }
          if (!plantaControl) {
            return;
          }

          if (isConductionSelected && this._assetName === 'MEL') {
            plantaControl.patchValue({ isEnabled: false, active: true });
          }
          else {
            plantaControl.patchValue({ isEnabled: true, active: false });
          }
        };

        const setVisibleToOnlyOperateCheck = () => {
          const isOperationSelected = licenseTypes.some((licenseType) => normalize(licenseType.name) === normalize('Operación') && licenseType.active);
          let onlyOperateCheckControl: FormControl | null | undefined;
          const conductionZonesControls = this.newEquipmentForm.controls.conductionZones as FormArray;
          for (let i = 0; i < conductionZonesControls.length; i++) {
            const control = conductionZonesControls.at(i);
            if (normalize(control.value.name) === normalize('Solo Operar')) {
              onlyOperateCheckControl = control as FormControl;
            }
          }

          if (!onlyOperateCheckControl) {
            return;
          }
          if (isOperationSelected) {
            if (onlyOperateCheckControl.value.isHidden) {
              onlyOperateCheckControl.patchValue({ isHidden: false, active: false });
            }
          }
          else {
            onlyOperateCheckControl.patchValue({ isHidden: true, active: false });
          }
        };

        setCheckToPlanta();
        setVisibleToOnlyOperateCheck();
      },
    });

    this.newEquipmentForm.controls.conductionZones.valueChanges.subscribe({
      next: (conductionZones: CheckFormControlValues[]) => {
        if (this.isModelAndBrandRequired) {
          const onlyOperateCheckValue = conductionZones
            .find((conductionZone) => normalize(conductionZone.name) === normalize('Solo Operar'));
          if (!onlyOperateCheckValue) {
            return;
          }

          const onlyOperateCheckStatus = onlyOperateCheckValue?.active;

          const everyOtherConductionZoneAreUnchecked = conductionZones
            .filter((conductionZone) => conductionZone.id !== onlyOperateCheckValue.id)
            .every((conductionZone) => conductionZone.active === false);

          const justOperationTypeIsChecked = !(this.newEquipmentForm.controls.licenseTypes.value as CheckFormControlValues[])
            .some((licenseType) => normalize(licenseType.name) !== normalize('Operación') && licenseType.active);

          if (onlyOperateCheckStatus && everyOtherConductionZoneAreUnchecked && justOperationTypeIsChecked) {
            this.setBrandAndModelValidators(false);
          }
          else {
            this.setBrandAndModelValidators(true);
          }
        }
      },
    });
  }

  onAddNewEquipment() {
    this.addEquipment.emit(this.newEquipmentForm.value as NewEquipmentRow);
    this.resetForm();
  }

  resetForm() {
    const { vehicleType, brand, model, licenseTypes, conductionZones } = this.newEquipmentForm.controls;
    vehicleType.patchValue('');
    brand.patchValue('');
    model.patchValue('');
    (licenseTypes as FormArray).controls.forEach((formGroup) => {
      const formGroupParsed = formGroup as FormGroup;
      formGroupParsed.controls.active.patchValue(false);
    });
    (conductionZones as FormArray).controls.forEach((formGroup) => {
      const formGroupParsed = formGroup as FormGroup;
      // const isActive = formGroupParsed.controls.name.value === 'Planta' && this.plantaDefaultSelected;
      formGroupParsed.controls.active.patchValue(false);
    });
  }

  getCheckControls(id: number, formType: 'LICENSE_TYPE' | 'ZONES' = 'ZONES') {
    let indexReq = -1;
    const formSectionType = formType === 'LICENSE_TYPE' ?
      this.asFormArray(this.newEquipmentForm.controls.licenseTypes)
      :
      this.asFormArray(this.newEquipmentForm.controls.conductionZones);

    formSectionType.value.some(({ id: rowIndex }: any, formArrayIndex: number) => {
      if (rowIndex === id) {
        indexReq = formArrayIndex;
      }
    });
    const formGroup = formSectionType.at(indexReq) as FormGroup;
    return formGroup.controls;
  }

  setBrandAndModelValidators(areRequired: boolean) {
    if (areRequired) {
      this.newEquipmentForm.controls.brand.setValidators(Validators.required);
      this.newEquipmentForm.controls.model.setValidators(Validators.required);
    }
    else {
      this.newEquipmentForm.controls.brand.clearValidators();
      this.newEquipmentForm.controls.model.clearValidators();
    }
    this.newEquipmentForm.controls.model.updateValueAndValidity();
    this.newEquipmentForm.controls.brand.updateValueAndValidity();
  }

  asFormControl(control: AbstractControl) {
    return control as FormControl;
  }

  asFormArray(control: AbstractControl) {
    return control as FormArray;
  }
}
